import { nextTick } from 'vue'
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
} from 'vue-router'
import LaunchLayout from '@/layouts/LaunchLayout.vue'
import WorkspaceLayout from '@/layouts/WorkspaceLayout.vue'
import SessionLayout from '@/layouts/SessionLayout.vue'
import { getPatientToken } from '@/lib/auth-token'
import { readRetired } from '@/lib/transfer-retirement'
import { isDocked, isDockFollower, isDockLeader, isNative } from '@/lib/platform'
import { logWarn } from '@/lib/log'

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * Which app a route belongs to. Platform IS the role here: the native
     * Capacitor build only serves the patient flow, the web build only serves
     * the therapist (it runs inside the portal iframe; there is no native
     * therapist build). The role guard below redirects single-role routes that
     * don't match the running build. `'either'` routes (training,
     * training-history, exercises-result) are shared via query params and are
     * never blocked. See CLAUDE.md "Platform = role boundary".
     *
     * `'docked'` is the Session App Dock's own axis, orthogonal to the above:
     * both roles in a dock run the *web* build, so platform cannot separate
     * them. A docked follower — the patient's device, holding no credentials —
     * may reach `'docked'` routes and nothing else. See the dock guard below.
     */
    role?: 'patient' | 'therapist' | 'either' | 'docked'
    /**
     * Reachable by the patient's side of a Session App Dock.
     *
     * Separate from `role` because it is a different axis: `/training` is a
     * `'either'` route that both builds run, AND the one screen a docked
     * follower is allowed to leave `/dock` for. The patient there runs the real
     * drill, so anything else about it is unchanged.
     */
    dockFollower?: boolean
    /**
     * This screen's job is to pick, connect or switch the patient.
     *
     * Meaningless inside a Session App Dock, where the session already names
     * one patient and the therapist is there to work with them. Marked rather
     * than listed so a new chooser is covered by saying what it is, not by
     * somebody remembering to add it to a guard.
     */
    choosesPatient?: boolean
    /** ExercisesPick: flips the picker to the read-only homework data source. */
    mode?: 'homework'
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes: [
    // Standalone embed page — no layout wrapper, served inside the portal iframe.
    // Must be listed before the layout groups so it doesn't inherit their guards.
    {
      path: '/embed',
      name: 'embed',
      component: () => import('@/views/embed/EmbedView.vue'),
      meta: { role: 'therapist' },
    },
    // Session App Dock entry — framed by the Videosprechstunde, not the portal.
    // Like /embed it sits outside the layout groups so it inherits no guards,
    // and it is reachable by both dock roles: the shell decides which we are.
    {
      path: '/dock',
      name: 'dock',
      component: () => import('@/views/dock/DockView.vue'),
      meta: { role: 'docked' },
    },
    {
      path: '/',
      component: LaunchLayout,
      children: [
        // Therapist
        {
          path: '',
          name: 'launch-initial',
          component: () => import('@/views/launch/therapist/LaunchInitial.vue'),
          // Native shell only ever serves the patient flow. Web stays on
          // LaunchInitial (therapist welcome). The first-launch redirect
          // here is what makes the onboarding screens reachable on iPad/
          // Android — without it, native users land on the therapist screen
          // because there's no native-aware index.html entry point.
          beforeEnter: () => {
            if (isNative) return { name: 'launch-first-start' }
          },
          meta: { role: 'therapist', choosesPatient: true },
        },
        {
          path: '/launch-patient-connected',
          name: 'launch-patient-connected',
          component: () => import('@/views/launch/therapist/LaunchPatientConnected.vue'),
          meta: { role: 'therapist', choosesPatient: true },
        },
        {
          path: '/launch-patient-notconnected',
          name: 'launch-patient-notconnected',
          component: () => import('@/views/launch/therapist/LaunchPatientNotConnected.vue'),
          meta: { role: 'therapist', choosesPatient: true },
        },
        {
          path: '/launch-trial',
          name: 'launch-trial',
          component: () => import('@/views/launch/therapist/LaunchTrial.vue'),
          meta: { role: 'therapist', choosesPatient: true },
        },
        // Patient
        {
          path: '/launch-first-start',
          name: 'launch-first-start',
          component: () => import('@/views/launch/patient/LaunchFirstStart.vue'),
          meta: { role: 'patient' },
        },
        {
          path: '/launch-terms',
          name: 'launch-terms',
          component: () => import('@/views/launch/patient/LaunchTerms.vue'),
          meta: { role: 'patient' },
        },
        {
          path: '/launch-trial-patient',
          name: 'launch-trial-patient',
          component: () => import('@/views/launch/patient/LaunchTrialPatient.vue'),
          meta: { role: 'patient' },
        },
        {
          path: '/launch-transferred',
          name: 'launch-transferred',
          component: () => import('@/views/launch/patient/LaunchTransferred.vue'),
          meta: { role: 'patient' },
        },
      ],
    },
    {
      path: '/',
      component: WorkspaceLayout,
      children: [
        {
          // Optional :portalPatientId lets the portal deep-link straight to a
          // patient (the Apps-tab "app starten" button) — the screen auto-selects
          // that patient once the caseload arrives. Bare path = manual picker.
          path: '/launch-choose-patient/:portalPatientId?',
          name: 'launch-choose-patient',
          component: () => import('@/views/workspace/LaunchChoosePatient.vue'),
          meta: { role: 'therapist', choosesPatient: true },
        },
        {
          path: '/launch-patient-settings',
          name: 'launch-patient-settings',
          component: () => import('@/views/workspace/LaunchPatientSettings.vue'),
          meta: { role: 'therapist' },
        },
        {
          path: '/launch-patient-topic-database',
          name: 'launch-patient-topic-database',
          component: () => import('@/views/workspace/LaunchPatientTopicOrDatabase.vue'),
          meta: { role: 'therapist' },
        },
        // The three legacy `launch-patient-topic-*` routes were retired when
        // the patient-side exercise-creation wizard (`/exercises-create`)
        // replaced them. See docs/screen-inventory.md.
        {
          path: '/launch-patient-database-createtraining',
          name: 'launch-patient-database-createtraining',
          component: () => import('@/views/workspace/LaunchPatientDatabaseCreateTraining.vue'),
          meta: { role: 'therapist' },
        },
        {
          path: '/launch-patient-database-phonemeselector',
          name: 'launch-patient-database-phonemeselector',
          component: () => import('@/views/workspace/LaunchPatientDatabasePhonemeSelector.vue'),
          meta: { role: 'therapist' },
        },
        {
          path: '/exercises-patient',
          name: 'exercises-patient',
          component: () => import('@/views/workspace/ExercisesPatient.vue'),
          meta: { role: 'therapist' },
        },
        {
          path: '/exercises-patient-selectitems',
          name: 'exercises-patient-selectitems',
          component: () => import('@/views/workspace/ExercisesPatientSelectItems.vue'),
          meta: { role: 'therapist' },
        },
        // Patient
        {
          path: '/exercises-create',
          name: 'exercises-create',
          component: () => import('@/views/workspace/patient/ExercisesCreate.vue'),
          meta: { role: 'patient' },
        },
        {
          path: '/exercises-pick',
          name: 'exercises-pick',
          component: () => import('@/views/workspace/patient/ExercisesPick.vue'),
          meta: { role: 'patient' },
        },
        {
          // Homework reuses the exact exercise picker, only the data source
          // differs. `meta.mode` flips ExercisesPick to the homework store
          // (read-only, no delete). See resources/js/views/workspace/patient/ExercisesPick.vue.
          path: '/exercises-pick-homework',
          name: 'exercises-pick-homework',
          component: () => import('@/views/workspace/patient/ExercisesPick.vue'),
          meta: { mode: 'homework', role: 'patient' },
        },
        {
          // Shared: shown to both the patient (own/homework drills) and the
          // therapist (assigned-exercise runs in the web app). Its internal
          // nav branches on the lastRun snapshot's `therapist` field — see
          // ExercisesResult.vue. Must stay 'either' so the role guard never
          // blocks the therapist.
          path: '/exercises-result',
          name: 'exercises-result',
          component: () => import('@/views/workspace/patient/ExercisesResult.vue'),
          meta: { role: 'either' },
        },
        // Run history. Re-registered 2026-06-16 for the therapist embed's
        // "ergebnisse ansehen" (?therapistExercise&patient&name). TrainingHistory.vue
        // serves both modes; the native patient path (?exercise) is dormant in the
        // app (no in-app link) but the route is harmless to the mobile build.
        {
          path: '/training-history',
          name: 'training-history',
          component: () => import('@/views/workspace/patient/TrainingHistory.vue'),
          // Shared: serves both the therapist embed (?therapistExercise&patient)
          // and the dormant native patient path (?exercise).
          meta: { role: 'either' },
        },
        {
          path: '/patient-settings',
          name: 'patient-settings',
          component: () => import('@/views/workspace/patient/PatientSettings.vue'),
          meta: { role: 'patient' },
        },
        // Legal / regulatory display screens, reached from the patient
        // Settings footer (Impressum, Regulatory information) and from the
        // bottom of Regulatory information (Open-source licenses).
        {
          path: '/impressum',
          name: 'impressum',
          component: () => import('@/views/workspace/patient/Impressum.vue'),
          meta: { role: 'patient' },
        },
        {
          path: '/regulatory-info',
          name: 'regulatory-info',
          component: () => import('@/views/workspace/patient/RegulatoryInfo.vue'),
          meta: { role: 'patient' },
        },
        {
          path: '/open-source-licenses',
          name: 'open-source-licenses',
          component: () => import('@/views/workspace/patient/OpenSourceLicenses.vue'),
          meta: { role: 'patient' },
        },
        // Therapist exercise library (practice-level)
        {
          path: '/therapist-exercises',
          name: 'therapist-exercises',
          component: () => import('@/views/workspace/therapist/TherapistExercisesList.vue'),
          meta: { role: 'therapist' },
        },
        {
          path: '/therapist-exercises-create',
          name: 'therapist-exercises-create',
          component: () => import('@/views/workspace/therapist/TherapistExercisesCreate.vue'),
          meta: { role: 'therapist' },
        },
        {
          path: '/therapist-exercises-create-themenbox',
          name: 'therapist-exercises-create-themenbox',
          component: () => import('@/views/workspace/therapist/TherapistExercisesCreateThemenbox.vue'),
          meta: { role: 'therapist' },
        },
        {
          path: '/therapist-exercises-create-datenbank',
          name: 'therapist-exercises-create-datenbank',
          component: () => import('@/views/workspace/therapist/TherapistExercisesCreateDatenbank.vue'),
          meta: { role: 'therapist' },
        },
        {
          path: '/therapist-exercises-create-items',
          name: 'therapist-exercises-create-items',
          component: () => import('@/views/workspace/therapist/TherapistExercisesCreateItems.vue'),
          meta: { role: 'therapist' },
        },
        {
          path: '/therapist-exercises-create-overview',
          name: 'therapist-exercises-create-overview',
          component: () => import('@/views/workspace/therapist/TherapistExercisesCreateOverview.vue'),
          meta: { role: 'therapist' },
        },
        // Experimental: type an IPA + record to see what the phoneme scorer
        // hears. Reachable via the flag-gated home entry (VITE_PHONEME_EVAL).
        {
          path: '/therapist-ipa-test',
          name: 'therapist-ipa-test',
          component: () => import('@/views/workspace/therapist/TherapistIpaTest.vue'),
          meta: { role: 'therapist' },
        },
      ],
    },
    {
      path: '/',
      component: SessionLayout,
      children: [
        {
          path: '/training',
          name: 'training',
          component: () => import('@/views/TrainingView.vue'),
          // Shared: native patient (?exercise / ?homework), therapist embed
          // (?therapistExercise&patient), and BOTH sides of a Session App Dock
          // — the patient runs this same drill, slaved to the therapist's.
          meta: { role: 'either', dockFollower: true },
        },
      ],
    },
  ],
})

/**
 * Patient onboarding gate. The two intro screens must show only on the
 * very first launch; once a patient eternal token has been minted (via
 * LaunchTerms.vue → auth.bootstrapPatient()) we skip both. On web,
 * `getPatientToken()` always resolves to null so this is a native-only
 * effect — see resources/js/lib/auth-token.ts.
 */
const ONBOARDING_ROUTES = new Set(['launch-first-start', 'launch-terms'])

router.beforeEach(async (to) => {
  if (typeof to.name === 'string' && ONBOARDING_ROUTES.has(to.name)) {
    const token = await getPatientToken()
    if (token) return { name: 'launch-trial-patient' }
  }
})

/**
 * Pure decision for the retirement gate below. Exported — like the role and
 * dock guards — so it can be unit-tested without booting the router.
 *
 * A device whose account was transferred away holds a revoked token, and
 * `lib/api.ts`'s 401 path records that fact locally before discarding it (see
 * lib/transfer-retirement.ts). From then on every patient route lands on the
 * one screen that can explain itself, rather than the app silently
 * re-bootstrapping into a new patient with a new trial.
 */
export function resolveRetirementGuard(
  toName: unknown,
  isRetired: boolean,
  role: 'patient' | 'therapist' | 'either' | 'docked' | undefined,
  native: boolean = isNative,
  dockFollower: boolean = isDockFollower(),
): { name: string } | undefined {
  if (!native || !isRetired) return undefined
  if (toName === 'launch-transferred') return undefined
  // Only the patient flow has anything to say here. A route reached inside a
  // dock belongs to somebody else's session and is not ours to redirect.
  if (role === 'docked' || dockFollower) return undefined
  return { name: 'launch-transferred' }
}

/**
 * Retirement gate.
 *
 * Runs after the onboarding gate so the "start fresh" button works: it clears
 * the marker and replaces to `launch-first-start`, which this guard then lets
 * through because the marker is gone.
 *
 * Ordered before the role guard for the same reason the onboarding gate is —
 * both are about which patient screen is correct, not about which build we are.
 */
router.beforeEach(async (to) => {
  // Cheap short-circuit before touching Preferences, which this guard would
  // otherwise read on every single navigation.
  if (!isNative || to.name === 'launch-transferred') return

  const retired = await readRetired()

  return resolveRetirementGuard(to.name, retired !== null, to.meta.role)
})

/**
 * Role boundary guard. Platform IS the role: the native Capacitor shell only
 * ever serves the patient flow, the web build only serves the therapist (it
 * runs inside the portal iframe; there is no native therapist build). So we
 * derive the running role from `isNative` rather than from auth state (which
 * only resolves post-bootstrap). When a route is single-role and that role
 * isn't the running one, we redirect to the running role's safe home rather
 * than render a screen that belongs to the other app — this stops the patient
 * (tablet) app from ever showing a therapist view and vice versa.
 *
 * The safe-home targets are themselves single-role for the running build, so
 * they re-pass this guard and can't loop. `'either'` routes (training,
 * training-history, exercises-result) are never blocked. Kept separate from
 * the async onboarding gate above: this one is synchronous and global.
 */
export const RUNNING_ROLE: 'patient' | 'therapist' = isNative ? 'patient' : 'therapist'
const ROLE_HOME = {
  patient: 'launch-trial-patient',
  therapist: 'launch-initial',
} as const

/**
 * The index route is the single platform entry hop: its own `beforeEnter`
 * forks by build (native ⇒ patient onboarding at `launch-first-start`, web ⇒
 * the therapist welcome it renders). Although the screen it renders is
 * therapist-only (`meta.role: 'therapist'`), the role guard must let it through
 * on *both* builds, otherwise on native it pre-empts that fork — global
 * `beforeEach` guards run before a route's `beforeEnter`, so blocking it here
 * would bounce a brand-new patient straight to `launch-trial-patient`, skipping
 * onboarding and the `auth.bootstrapPatient()` call that mints their token.
 */
const ENTRY_ROUTE = 'launch-initial'

/**
 * Pure decision for the role guard: returns the redirect target when a route's
 * `meta.role` is single-role and doesn't match the running build, or
 * `undefined` to allow navigation. Exported (and `runningRole` injectable) so
 * the logic is unit-testable without booting the whole router.
 */
export function resolveRoleGuard(
  role: 'patient' | 'therapist' | 'either' | 'docked' | undefined,
  toName: unknown,
  runningRole: 'patient' | 'therapist' = RUNNING_ROLE,
): { name: string } | undefined {
  // A dock route belongs to neither build's role axis; the dock guard below
  // owns it. Nothing else may reach it, because nothing else has a shell.
  if (role === 'docked') return isDocked ? undefined : { name: ROLE_HOME[runningRole] }
  if (role === undefined || role === 'either' || role === runningRole) return undefined
  if (toName === ENTRY_ROUTE) return undefined // platform entry hop — its beforeEnter forks by build
  const target = ROLE_HOME[runningRole]
  if (toName === target) return undefined // already heading to the safe home — avoid a loop
  return { name: target }
}

/**
 * The follower's confinement, and the reason it is a router guard rather than a
 * convention.
 *
 * The patient's side of a dock runs the same web bundle as the therapist's, on
 * the same origin, with every therapist screen compiled in. What separates them
 * is that the follower holds no session — so a stray navigation there would
 * render a therapist screen that then fails every API call, in front of a
 * patient, mid-consultation. Confining it turns "it would not work anyway" into
 * "it cannot be reached", which is the form the claim has to take. See the dock
 * concept §5.3 and its checklist in §10.
 *
 * Two routes are allowed: `/dock` itself, and `/training` — which is the whole
 * point of the feature. The drill runs there in follower mode, driven by the
 * therapist's side and writing nothing of its own.
 *
 * Fail-closed: `isDockFollower()` treats an unknown role as the follower, so a
 * handshake that never completed confines rather than opens up.
 *
 * Exported for unit testing without booting the router.
 */
export function resolveDockGuard(
  role: 'patient' | 'therapist' | 'either' | 'docked' | undefined,
  dockFollower: boolean | undefined = false,
  follower: boolean = isDockFollower(),
): { name: string } | undefined {
  if (!follower) return undefined
  if (role === 'docked' || dockFollower === true) return undefined
  return { name: 'dock' }
}

/**
 * The leader's confinement: a dock session is bound to one patient, so the
 * therapist's side may not walk back out to pick a different one.
 *
 * The follower guard above exists because the patient holds no credentials.
 * This one is not that. The therapist is properly authenticated and the server
 * already refuses another patient's data - `ScopeToPatient` answers 403 for
 * anything outside the session's scope. What is wrong with reaching the
 * chooser is not that it is dangerous but that it is a lie: it offers a
 * caseload that this session cannot open, and a therapist who picks from it
 * gets a screen of failures mid-consultation. Taking the exit away is how the
 * app stops promising something it will not do.
 *
 * Back to the exercise list, which is where the dock puts the therapist to
 * begin with.
 *
 * Exported for unit testing without booting the router.
 */
export function resolveDockLeaderGuard(
  choosesPatient: boolean | undefined,
  leader: boolean = isDockLeader(),
): { name: string } | undefined {
  if (!leader || choosesPatient !== true) return undefined
  return { name: 'exercises-patient' }
}

router.beforeEach((to) => {
  const leaderRedirect = resolveDockLeaderGuard(to.meta.choosesPatient)
  if (leaderRedirect !== undefined) {
    logWarn(
      `[router] blocked ${String(to.name)} for a docked leader: the session names its patient`,
    )
    return leaderRedirect
  }

  const dockRedirect = resolveDockGuard(to.meta.role, to.meta.dockFollower)
  if (dockRedirect !== undefined) {
    logWarn(
      `[router] blocked ${String(to.name)} (role=${to.meta.role}) for a docked follower`,
    )
    return dockRedirect
  }

  const redirect = resolveRoleGuard(to.meta.role, to.name)
  if (redirect === undefined) return
  logWarn(
    `[router] blocked ${String(to.name)} (role=${to.meta.role}) on ${RUNNING_ROLE} build; redirecting to ${redirect.name}`,
  )
  return redirect
})

/**
 * Cross-layout view transition via the native View Transitions API.
 *
 * Why not Vue's `<Transition>` around the top-level RouterView: that approach
 * keeps the leaving layout instance in the DOM during its leave animation,
 * but vue-router's globally-provided route updates the moment navigation
 * commits. The leaving layout's inner `<RouterView />` then re-resolves
 * against the new route and renders null, which tears the leaving screen
 * down mid-animation. In practice (and as we just saw) it can also leave
 * the new layout stuck in its enter-from state — white screen, no recovery.
 *
 * The View Transitions API sidesteps the problem: it snapshots the DOM
 * *before* the route change, lets Vue tear down + remount the full subtree
 * normally, then crossfades the captured snapshots. No live Vue tree fights
 * over the route during the animation.
 *
 * Scoped to cross-layout navigations only — within a layout, the existing
 * chrome (logo header, frog background, etc.) already provides continuity
 * and a crossfade would be visual noise. Layouts are identified by the
 * top-level matched component reference, which is stable because the
 * layout SFCs are statically imported above.
 *
 * Fallbacks:
 *   - No `startViewTransition`: skip silently, navigation behaves as before.
 *   - First navigation (no `from.name`): no captured "from" frame to fade
 *     from, so we don't try.
 *   - `prefers-reduced-motion`: handled in CSS via `::view-transition-*`
 *     (see resources/css/app.css), which also gives users a single place
 *     to tune the animation.
 */
type StartViewTransition = (cb: () => Promise<void> | void) => unknown
function getStartViewTransition(): StartViewTransition | null {
  // The View Transitions API isn't in older TS DOM libs and isn't in older
  // WebKit; feature-detect through an indexed access rather than typing it.
  const fn = (document as unknown as { startViewTransition?: StartViewTransition })
    .startViewTransition
  return typeof fn === 'function' ? fn.bind(document) : null
}

function isCrossLayout(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
  // `matched[0]` is the parent layout-route in our three-layout table. The
  // `.components.default` reference is stable because layout SFCs are
  // statically imported at the top of this file.
  const fromLayout = from.matched[0]?.components?.default
  const toLayout = to.matched[0]?.components?.default
  return fromLayout !== undefined && toLayout !== undefined && fromLayout !== toLayout
}

router.beforeResolve((to, from) => {
  if (!from.name) return // first navigation — nothing to fade from
  if (!isCrossLayout(to, from)) return
  // A hidden document cannot capture a frame: startViewTransition rejects with
  // "Transition was aborted because of invalid state". Ordinary tabs rarely
  // navigate while hidden, but a docked app does routinely — the therapist
  // switches the room back to video and the app pane is hidden while the app
  // keeps navigating. Skip the crossfade rather than throw into their console.
  if (typeof document !== 'undefined' && document.hidden) return

  const start = getStartViewTransition()
  if (!start) return

  // Pause navigation until startViewTransition has captured the old frame.
  // Inside the callback we resolve so vue-router commits the route, then
  // await `nextTick` so Vue's render flushes before the browser snapshots
  // the new frame.
  return new Promise<void>((resolve) => {
    start(async () => {
      resolve()
      await nextTick()
    })
  })
})

export default router
